/**
 * API methods for expense reports (Department Head visibility)
 *
 * Provides methods for Department Heads to view expense reports
 * submitted against their approved Expenditure Requests.
 */

import type {
  ExpenseReportSummary,
  ExpenseReportDetail,
} from '../types/expense-tracker';
import { getAccessToken } from './auth-store';

/**
 * Simple API error class
 */
export class ApiError extends Error {
  constructor(
    message: string,
    public status: number = 0
  ) {
    super(message);
    this.name = 'ApiError';
  }
}

/**
 * Get auth headers for API requests
 */
function getAuthHeaders(): HeadersInit {
  const token = getAccessToken();
  return {
    'Authorization': `Bearer ${token}`,
    'Content-Type': 'application/json',
  };
}

/**
 * Get all expense reports for requests the current user approved (Department Head role)
 * @returns Promise with array of expense report summaries
 * @throws ApiError if request fails or user lacks permission
 */
export async function getExpenseReportsForApprover(): Promise<ExpenseReportSummary[]> {
  const response = await fetch('/api/expense-reports?role=approver', {
    headers: getAuthHeaders(),
  });

  if (response.status === 403) {
    throw new ApiError('Access denied to expense reports', 403);
  }

  if (!response.ok) {
    throw new ApiError(
      `Failed to fetch expense reports: ${response.statusText}`,
      response.status
    );
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get full details for a specific expense report
 * @param reportId - The expense report ID
 * @returns Promise with expense report details including receipts
 * @throws ApiError if request fails or user lacks permission
 */
export async function getExpenseReportDetails(reportId: string): Promise<ExpenseReportDetail> {
  const response = await fetch(`/api/expense-reports/${reportId}`, {
    headers: getAuthHeaders(),
  });

  if (response.status === 403) {
    throw new ApiError('Access denied to this expense report', 403);
  }

  if (!response.ok) {
    throw new ApiError(
      `Failed to fetch expense report: ${response.statusText}`,
      response.status
    );
  }

  const data = await response.json();
  return data.data;
}

/**
 * Get expense reports for a specific expenditure request
 * @param requestId - The expenditure request ID
 * @returns Promise with array of expense report summaries
 * @throws ApiError if request fails
 */
export async function getExpenseReportsForRequest(requestId: string): Promise<ExpenseReportSummary[]> {
  const response = await fetch(`/api/expense-reports?requestId=${requestId}`, {
    headers: getAuthHeaders(),
  });

  if (!response.ok) {
    throw new ApiError(
      `Failed to fetch expense reports for request: ${response.statusText}`,
      response.status
    );
  }

  const data = await response.json();
  return data.data;
}
